import { TestOrgNumberButton } from '../components/TestOrgNumberButton';
import { withTestOrgNumber } from '../utils/companyIdentifiers';
import { AddressAutocomplete, applySelectedAddress } from '../components/AddressAutocomplete';
import { PhoneInput } from '../components/PhoneInput';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { adminApi } from '../services/api';
import type { AdminInspector } from '../types';
import ReactQuill from 'react-quill';
import { ProviderThemeEditor } from '../components/company-theme/ProviderThemeEditor';
import 'react-quill/dist/quill.snow.css';

interface InspectorFormData {
  name: string;
  email: string;
  inspectorCompanyName: string;
  organizationNumber: string;
  title: string;
  phone: string;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  vatNumber: string;
  website: string;
  hourlyRate: string;
  experienceYears: number;
  responseTime: string;
  geographicArea: string;
  leadTime: string;
  fixedPriceAvailable: boolean;
  initialMeetingFree: boolean;
  availability: boolean;
  specialties: string;
  languages: string;
  verifiedInspector: string;
  notes: string;
  inspectorPresentation: string;
}

function inspectorToFormData(inspector: AdminInspector): InspectorFormData {
  return {
    name: inspector.name || '',
    email: inspector.email || '',
    inspectorCompanyName: inspector.inspectorCompanyName || '',
    organizationNumber: inspector.organizationNumber || '',
    title: inspector.title || '',
    phone: inspector.phone || '',
    address: inspector.address || '',
    postalCode: inspector.postalCode || '',
    city: inspector.city || '',
    country: inspector.country || '',
    vatNumber: inspector.vatNumber || '',
    website: inspector.website || '',
    hourlyRate: inspector.hourlyRate || '',
    experienceYears: inspector.experienceYears ?? 0,
    responseTime: inspector.responseTime || '',
    geographicArea: inspector.geographicArea || '',
    leadTime: inspector.leadTime || '',
    fixedPriceAvailable: inspector.fixedPriceAvailable ?? false,
    initialMeetingFree: inspector.initialMeetingFree ?? false,
    availability: inspector.availability ?? false,
    specialties: (inspector.specialties || []).join(', '),
    languages: (inspector.languages || []).join(', '),
    verifiedInspector: inspector.verifiedInspector || '',
    notes: inspector.notes || '',
    inspectorPresentation: inspector.inspectorPresentation || '',
  };
}

const initialFormData: InspectorFormData = {
  name: '',
  email: '',
  inspectorCompanyName: '',
  organizationNumber: '',
  title: '',
  phone: '',
  address: '',
  postalCode: '',
  city: '',
  country: '',
  vatNumber: '',
  website: '',
  hourlyRate: '',
  experienceYears: 0,
  responseTime: '',
  geographicArea: '',
  leadTime: '',
  fixedPriceAvailable: false,
  initialMeetingFree: false,
  availability: false,
  specialties: '',
  languages: '',
  verifiedInspector: '',
  notes: '',
  inspectorPresentation: '',
};

export function AdminInspectorDetailView() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [inspector, setInspector] = useState<AdminInspector | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [formData, setFormData] = useState<InspectorFormData>(initialFormData);
  const [activeTab, setActiveTab] = useState<'overview' | 'practical' | 'theme'>('overview');

  const basePath = '/admin/inspectors';

  useEffect(() => {
    if (id) {
      loadInspector(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadInspector = async (inspectorId: string) => {
    try {
      setIsLoading(true);
      const inspectors = await adminApi.getAllInspectors();
      const found = inspectors.find(l => l.id === inspectorId);
      if (found) {
        setInspector(found);
        setFormData(inspectorToFormData(found));
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('common.error') });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setMessage(null);
  };

  const handleCancelEdit = () => {
    if (inspector) {
      setFormData(inspectorToFormData(inspector));
    }
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!id || !inspector) return;
    try {
      setIsSaving(true);
      const payload = {
        ...formData,
        specialties: formData.specialties
          .split(',')
          .map(s => s.trim())
          .filter(Boolean),
        languages: formData.languages
          .split(',')
          .map(s => s.trim())
          .filter(Boolean),
      };
      const updated = await adminApi.updateInspector(id, payload as any);
      setInspector(updated);
      setFormData(inspectorToFormData(updated));
      setIsEditing(false);
      setMessage({ type: 'success', text: t('common.saved') });
    } catch (err) {
      setMessage({ type: 'error', text: t('common.error') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof InspectorFormData, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('sv-SE');
    } catch {
      return dateStr;
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!inspector) {
    return (
      <DashboardLayout>
        <div className="alert alert-danger">{t('common.error')}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Page Header */}
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">
              {t('adminInspectorView.title')} - {inspector.inspectorNumber}
            </h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: t('adminInspectorView.breadcrumbInspectors'), path: basePath },
            { label: inspector.name }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('adminInspectorView.description')}</p>
        </div>
        <div className="d-flex justify-content-end mt-2">
          <button className="btn btn-light-brand" onClick={() => navigate(basePath)}>
            <i className="feather-arrow-left me-2"></i>
            {t('common.back')}
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="mb-4" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <ul className="nav nav-tabs nav-tabs-custom-style flex-nowrap" role="tablist" style={{ minWidth: 'max-content' }}>
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
              <i className="feather-clipboard d-sm-none me-0"></i>
              <span className="d-none d-sm-inline">{t('adminInspectorView.tab.overview')}</span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'practical' ? 'active' : ''}`} onClick={() => setActiveTab('practical')}>
              <i className="feather-info d-sm-none me-0"></i>
              <span className="d-none d-sm-inline">{t('marketplace.provider.practicalInfo', 'Praktisk information')}</span>
            </button>
          </li>
          {/* Admin changes the theme on the expert's behalf; every change is logged (ClickUp 869fa6m3u). */}
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'theme' ? 'active' : ''}`} onClick={() => setActiveTab('theme')} data-testid="admin-provider-theme-tab">
              <i className="feather-droplet d-sm-none me-0" aria-hidden="true"></i>
              <span className="d-none d-sm-inline">{t('settings.theme.title')}</span>
              <span className="visually-hidden d-sm-none">{t('settings.theme.title')}</span>
            </button>
          </li>
        </ul>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <Card>
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h5 className="card-title mb-0">{t('adminInspectorView.tab.overview')}</h5>
            {!isEditing && (
              <button type="button" className="btn btn-primary" onClick={handleEdit} aria-label={t('common.change', 'Ändra')}>
                <i className="feather-edit me-2"></i>{t('common.change', 'Ändra')}
              </button>
            )}
          </div>
          <div className="card-body">
            {/* === Grunduppgifter === */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('adminInspectors.columns.inspectorNumber', 'Besiktningsmansnummer')}</label>
                <input type="text" className="form-control" value={inspector.inspectorNumber ?? ''} disabled />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.verifiedInspector', 'Status')}</label>
                {isEditing ? (
                  <select className="form-select" value={formData.verifiedInspector} onChange={(e) => handleChange('verifiedInspector', e.target.value)}>
                    <option value="Ej Verifierad">{t('adminInspectorView.statusNotVerified')}</option>
                    <option value="Verifierad">{t('adminInspectorView.statusVerified')}</option>
                  </select>
                ) : (
                  <div><span className={`badge bg-${inspector.verifiedInspector === 'Verifierad' ? 'success' : 'secondary'}`}>{inspector.verifiedInspector || '-'}</span></div>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.name', 'Namn')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.name : (inspector.name || '')} disabled={!isEditing} onChange={(e) => handleChange('name', e.target.value)} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.organizationNumber', 'Org.Nr')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.organizationNumber : (inspector.organizationNumber || '')} disabled={!isEditing} onChange={(e) => handleChange('organizationNumber', e.target.value)} />
                <TestOrgNumberButton disabled={!isEditing} onGenerated={value => setFormData(previous => withTestOrgNumber(previous, value))} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.inspectorCompanyName', 'Bolagsnamn')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.inspectorCompanyName : (inspector.inspectorCompanyName || '')} disabled={!isEditing} onChange={(e) => handleChange('inspectorCompanyName', e.target.value)} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.phone', 'Telefon')}</label>
                <PhoneInput name="phone" className="form-control" value={isEditing ? formData.phone : (inspector.phone || '')} disabled={!isEditing} onChange={(e) => handleChange('phone', e.target.value)} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.vatNumber', 'Momsregistreringsnummer')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.vatNumber : (inspector.vatNumber || '')} disabled={!isEditing} onChange={(e) => handleChange('vatNumber', e.target.value)} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.email', 'E-post')}</label>
                <input type="email" className="form-control" value={isEditing ? formData.email : (inspector.email || '')} disabled={!isEditing} onChange={(e) => handleChange('email', e.target.value)} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.website', 'Webbplats')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.website : (inspector.website || '')} disabled={!isEditing} onChange={(e) => handleChange('website', e.target.value)} />
              </div>
            </div>

            {/* === Adress === */}
            <hr className="my-3" />
            <h6 className="fw-bold mb-3">{t('inspectors.columns.address', 'Adress')}</h6>

            <div className="row">
              <div className="col-md-6 mb-3">
                <AddressAutocomplete
                  name="address"
                  label={t('inspectors.columns.address', 'Gatuadress')}
                  onAddressSelect={address => setFormData(previous => applySelectedAddress(previous, address))}
                  type="text"
                  value={isEditing ? formData.address : (inspector.address || '')}
                  disabled={!isEditing}
                  onChange={(e) => handleChange('address', e.target.value)}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.postalCode', 'Postkod')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.postalCode : (inspector.postalCode || '')} disabled={!isEditing} onChange={(e) => handleChange('postalCode', e.target.value)} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.city', 'Stad')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.city : (inspector.city || '')} disabled={!isEditing} onChange={(e) => handleChange('city', e.target.value)} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.country', 'Land')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.country : (inspector.country || '')} disabled={!isEditing} onChange={(e) => handleChange('country', e.target.value)} />
              </div>
            </div>

            {/* === Datum === */}
            <hr className="my-3" />
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('adminInspectors.columns.createdAt', 'Skapad')}</label>
                <input type="text" className="form-control" value={formatDate(inspector.createdAt)} disabled />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('adminInspectors.columns.updatedAt', 'Uppdaterad')}</label>
                <input type="text" className="form-control" value={formatDate(inspector.updatedAt)} disabled />
              </div>
            </div>

            {/* === Besiktningsmanspresentation (rich text) === */}
            <hr className="my-3" />
            <div className="mb-3">
              <label className="form-label">{t('inspectors.columns.inspectorPresentation', 'Besiktningsmanspresentation')}</label>
              {isEditing ? (
                <>
                  <ReactQuill theme="snow" value={formData.inspectorPresentation} onChange={(value) => handleChange('inspectorPresentation', value)} style={{ height: '150px' }} />
                  <div style={{ height: '45px' }}></div>
                </>
              ) : (
                inspector.inspectorPresentation ? (
                  <div className="form-control bg-light" style={{ minHeight: '60px', height: 'auto' }}>
                    <div className="ql-editor p-0" dangerouslySetInnerHTML={{ __html: inspector.inspectorPresentation }} />
                  </div>
                ) : (
                  <input type="text" className="form-control" value="" disabled />
                )
              )}
            </div>

            {/* === Anteckningar (rich text) === */}
            <div className="mb-3">
              <label className="form-label">{t('inspectors.columns.notes', 'Anteckningar')}</label>
              {isEditing ? (
                <>
                  <ReactQuill theme="snow" value={formData.notes} onChange={(value) => handleChange('notes', value)} style={{ height: '150px' }} />
                  <div style={{ height: '45px' }}></div>
                </>
              ) : (
                inspector.notes ? (
                  <div className="form-control bg-light" style={{ minHeight: '60px', height: 'auto' }}>
                    <div className="ql-editor p-0" dangerouslySetInnerHTML={{ __html: inspector.notes }} />
                  </div>
                ) : (
                  <input type="text" className="form-control" value="" disabled />
                )
              )}
            </div>

            {/* Action Buttons */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              {isEditing ? (
                <>
                  <button className="btn btn-secondary" onClick={handleCancelEdit} disabled={isSaving}>
                    {t('common.cancel')}
                  </button>
                  <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                    {t('common.save')}
                  </button>
                </>
              ) : (
                <button className="btn btn-secondary" onClick={() => navigate(basePath)}>
                  {t('common.cancel')}
                </button>
              )}
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'theme' && id && (
        <Card>
          <div className="card-body">
            <ProviderThemeEditor type="inspector" userId={id} profile={inspector ? { geographicArea: inspector.geographicArea, languages: inspector.languages ?? [] } : null} />
          </div>
        </Card>
      )}

      {activeTab === 'practical' && (
        <Card>
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h5 className="card-title mb-0">{t('marketplace.provider.practicalInfo', 'Praktisk information')}</h5>
            {!isEditing && (
              <button type="button" className="btn btn-primary" onClick={handleEdit} aria-label={t('common.change', 'Ändra')}>
                <i className="feather-edit me-2"></i>{t('common.change', 'Ändra')}
              </button>
            )}
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.title', 'Titel')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.title : (inspector.title || '')} disabled={!isEditing} onChange={(e) => handleChange('title', e.target.value)} placeholder={t('adminInspectorView.placeholderTitle')} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.experienceYears', 'Erfarenhetsår')}</label>
                <input type="number" className="form-control" value={isEditing ? formData.experienceYears : (inspector.experienceYears ?? 0)} disabled={!isEditing} onChange={(e) => handleChange('experienceYears', parseInt(e.target.value, 10) || 0)} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.specialties', 'Specialistområden')}</label>
                {isEditing ? (
                  <>
                    <input type="text" className="form-control" value={formData.specialties} onChange={(e) => handleChange('specialties', e.target.value)} placeholder={t('adminInspectorView.placeholderSpecialties')} />
                    <small className="text-muted">{t('common.commaSeparated', 'Kommaseparerade värden')}</small>
                  </>
                ) : (
                  <div>{(inspector.specialties || []).length > 0 ? (inspector.specialties || []).map((s, i) => <span key={i} className="badge bg-primary me-1 mb-1">{s}</span>) : <span className="text-muted">-</span>}</div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.languages', 'Språk')}</label>
                {isEditing ? (
                  <>
                    <input type="text" className="form-control" value={formData.languages} onChange={(e) => handleChange('languages', e.target.value)} placeholder={t('adminInspectorView.placeholderLanguages')} />
                    <small className="text-muted">{t('common.commaSeparated', 'Kommaseparerade värden')}</small>
                  </>
                ) : (
                  <div>{(inspector.languages || []).length > 0 ? (inspector.languages || []).map((l, i) => <span key={i} className="badge bg-info me-1 mb-1">{l}</span>) : <span className="text-muted">-</span>}</div>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.hourlyRate', 'Timarvode')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.hourlyRate : (inspector.hourlyRate || '')} disabled={!isEditing} onChange={(e) => handleChange('hourlyRate', e.target.value)} placeholder={t('adminInspectorView.placeholderHourlyRate')} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.responseTime', 'Svarstid')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.responseTime : (inspector.responseTime || '')} disabled={!isEditing} onChange={(e) => handleChange('responseTime', e.target.value)} placeholder={t('adminInspectorView.placeholderResponseTime')} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.geographicArea', 'Geografiskt område')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.geographicArea : (inspector.geographicArea || '')} disabled={!isEditing} onChange={(e) => handleChange('geographicArea', e.target.value)} placeholder={t('adminInspectorView.placeholderGeographicArea')} />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('inspectors.columns.leadTime', 'Ledtid')}</label>
                <input type="text" className="form-control" value={isEditing ? formData.leadTime : (inspector.leadTime || '')} disabled={!isEditing} onChange={(e) => handleChange('leadTime', e.target.value)} placeholder={t('adminInspectorView.placeholderLeadTime')} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">{t('inspectors.columns.fixedPriceAvailable', 'Fast pris möjligt')}</label>
                {isEditing ? (
                  <div className="form-check mt-2">
                    <input type="checkbox" className="form-check-input" id="fixedPriceAvailable" checked={formData.fixedPriceAvailable} onChange={(e) => handleChange('fixedPriceAvailable', e.target.checked)} />
                    <label className="form-check-label" htmlFor="fixedPriceAvailable">{formData.fixedPriceAvailable ? t('adminInspectorView.yes') : t('adminInspectorView.no')}</label>
                  </div>
                ) : (
                  <div><span className={`badge bg-${inspector.fixedPriceAvailable ? 'success' : 'secondary'}`}>{inspector.fixedPriceAvailable ? t('adminInspectorView.yes') : t('adminInspectorView.no')}</span></div>
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">{t('inspectors.columns.initialMeetingFree', 'Gratis inledande möte')}</label>
                {isEditing ? (
                  <div className="form-check mt-2">
                    <input type="checkbox" className="form-check-input" id="initialMeetingFree" checked={formData.initialMeetingFree} onChange={(e) => handleChange('initialMeetingFree', e.target.checked)} />
                    <label className="form-check-label" htmlFor="initialMeetingFree">{formData.initialMeetingFree ? t('adminInspectorView.yes') : t('adminInspectorView.no')}</label>
                  </div>
                ) : (
                  <div><span className={`badge bg-${inspector.initialMeetingFree ? 'success' : 'secondary'}`}>{inspector.initialMeetingFree ? t('adminInspectorView.yes') : t('adminInspectorView.no')}</span></div>
                )}
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">{t('inspectors.columns.availability', 'Tillgänglig för nya uppdrag')}</label>
                {isEditing ? (
                  <div className="form-check mt-2">
                    <input type="checkbox" className="form-check-input" id="availability" checked={formData.availability} onChange={(e) => handleChange('availability', e.target.checked)} />
                    <label className="form-check-label" htmlFor="availability">{formData.availability ? t('adminInspectorView.yes') : t('adminInspectorView.no')}</label>
                  </div>
                ) : (
                  <div><span className={`badge bg-${inspector.availability ? 'success' : 'secondary'}`}>{inspector.availability ? t('adminInspectorView.yes') : t('adminInspectorView.no')}</span></div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              {isEditing ? (
                <>
                  <button className="btn btn-secondary" onClick={handleCancelEdit} disabled={isSaving}>
                    {t('common.cancel')}
                  </button>
                  <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                    {t('common.save')}
                  </button>
                </>
              ) : (
                <button className="btn btn-secondary" onClick={() => navigate(basePath)}>
                  {t('common.cancel')}
                </button>
              )}
            </div>
          </div>
        </Card>
      )}
    </DashboardLayout>
  );
}
